// BUSINESS DESK — thème dynamique : extrait la couleur dominante du logo et adapte l'accent.
const VARS = ["--dk-a1", "--dk-a2", "--dk-a1-rgb", "--dk-tint", "--dk-tint2", "--dk-muted", "--dk-ondark", "--dk-ondark2"];
const CACHE_KEY = "desk_palette";

function hslToRgb(h, s, l) {
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    return Math.round(255 * (l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))));
  };
  return [f(0), f(8), f(4)];
}
const hsl = (h, s, l) => {
  const [r, g, b] = hslToRgb(h, s, l);
  return `rgb(${r},${g},${b})`;
};

function paletteFromHueSat(h, s) {
  const sa = Math.min(Math.max(s, 0.35), 0.85);
  return {
    "--dk-a1": hsl(h, sa, 0.34),
    "--dk-a2": hsl(h, Math.min(sa + 0.1, 0.92), 0.48),
    "--dk-a1-rgb": hslToRgb(h, sa, 0.34).join(","),
    "--dk-tint": hsl(h, 0.55, 0.93),
    "--dk-tint2": hsl(h, 0.5, 0.97),
    "--dk-muted": hsl(h, 0.3, 0.38),
    "--dk-ondark": hsl(h, 0.55, 0.8),
    "--dk-ondark2": hsl(h, 0.35, 0.62),
  };
}

export function applyPalette(palette) {
  const root = document.documentElement;
  if (!palette) {
    VARS.forEach((v) => root.style.removeProperty(v));
    return;
  }
  Object.entries(palette).forEach(([k, v]) => root.style.setProperty(k, v));
}

export function applyCachedPalette() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) applyPalette(JSON.parse(raw));
  } catch { /* cache illisible */ }
}

export function resetPalette() {
  applyPalette(null);
}

export function clearPaletteCache() {
  try { localStorage.removeItem(CACHE_KEY); } catch { /* privé */ }
  resetPalette();
}

// Couleur dominante : moyenne vectorielle des teintes des pixels saturés du logo.
export async function applyLogoTheme(logoUrl) {
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = logoUrl;
    });
    const size = 36;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, size, size);
    const { data } = ctx.getImageData(0, 0, size, size);
    let sumX = 0, sumY = 0, sumS = 0, count = 0, opaque = 0;
    for (let i = 0; i < data.length; i += 4) {
      const [r, g, b, a] = [data[i] / 255, data[i + 1] / 255, data[i + 2] / 255, data[i + 3]];
      if (a < 128) continue;
      opaque++;
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      const l = (max + min) / 2;
      const d = max - min;
      if (d < 0.001 || l < 0.12 || l > 0.92) continue;
      const s = d / (1 - Math.abs(2 * l - 1));
      if (s < 0.22) continue;
      let h;
      if (max === r) h = ((g - b) / d) % 6;
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h = (h * 60 + 360) % 360;
      const rad = (h * Math.PI) / 180;
      sumX += Math.cos(rad);
      sumY += Math.sin(rad);
      sumS += s;
      count++;
    }
    // Logo trop neutre (noir/blanc/gris) → on garde l'or par défaut.
    if (!opaque || count / opaque < 0.04) {
      clearPaletteCache();
      return;
    }
    const hue = ((Math.atan2(sumY, sumX) * 180) / Math.PI + 360) % 360;
    const palette = paletteFromHueSat(hue, sumS / count);
    applyPalette(palette);
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(palette)); } catch { /* privé */ }
  } catch {
    /* extraction impossible (CORS, image corrompue) → thème par défaut */
  }
}
